import { useEffect, useMemo, useRef, useState } from "react";
import { Search, CheckCircle2, ArrowRight } from "lucide-react";
import { CarDiagram } from "../../components/CarDiagram";
import GamesPageShell from "../../components/games/GamesPageShell";
import GameResultCard from "../../components/games/GameResultCard";
import { SPOT_THE_ISSUE_SCENES, FIND_THE_DIFFERENCE_SCENE } from "../../content/games";
import { trackGameEvent } from "../../lib/gameEvents";

// Rounds: the two authored "Spot the Issue" scenes, then the "Find the
// Difference" variant, played back to back as one game.
const ROUNDS = [
  ...SPOT_THE_ISSUE_SCENES.map((scene) => ({ type: "spot", scene })),
  { type: "diff", scene: FIND_THE_DIFFERENCE_SCENE },
];

function distancePct(a, b) {
  return Math.hypot(a.x_pct - b.x_pct, a.y_pct - b.y_pct);
}

// Keyboard-equivalent for tapping the diagram. The diagram itself is a
// free-form pointer surface (tap anywhere), which has no meaningful
// keyboard equivalent, so instead we expose the SAME authored issue
// coordinates as real, focusable, labeled "check point" buttons (guaranteed
// hits), interleaved with decoy points kept well clear of every hotspot
// (guaranteed misses, so keyboard users get genuine hit/miss feedback and
// still have to explore rather than being handed a spoiler list). Order is
// a deterministic hash, not authoring order and not literal alternation,
// so the real/decoy pattern isn't trivially guessable while staying stable
// for tests.
const DECOY_CANDIDATES = [
  { x_pct: 10, y_pct: 20 }, { x_pct: 90, y_pct: 20 },
  { x_pct: 10, y_pct: 80 }, { x_pct: 90, y_pct: 80 },
  { x_pct: 50, y_pct: 10 }, { x_pct: 50, y_pct: 90 },
  { x_pct: 15, y_pct: 50 }, { x_pct: 85, y_pct: 50 },
  { x_pct: 35, y_pct: 15 }, { x_pct: 65, y_pct: 15 },
  { x_pct: 35, y_pct: 85 }, { x_pct: 65, y_pct: 85 },
  { x_pct: 20, y_pct: 35 }, { x_pct: 80, y_pct: 35 },
  { x_pct: 50, y_pct: 50 },
];
const DECOY_CLEARANCE_PCT = 6; // extra margin beyond an issue's own radiusPct

function hashId(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

function buildKeyboardTargets(items) {
  const decoys = [];
  for (const c of DECOY_CANDIDATES) {
    if (decoys.length >= items.length) break;
    const clear = items.every((it) => distancePct(c, it) >= it.radiusPct + DECOY_CLEARANCE_PCT);
    if (clear) decoys.push({ id: `decoy-${decoys.length}`, x_pct: c.x_pct, y_pct: c.y_pct, isDecoy: true });
  }
  const real = items.map((it) => ({ id: it.id, x_pct: it.x_pct, y_pct: it.y_pct, isDecoy: false }));
  return [...real, ...decoys].sort((a, b) => hashId(a.id) - hashId(b.id));
}

export default function SpotTheIssueGame() {
  const [roundIndex, setRoundIndex] = useState(0);
  const [foundIds, setFoundIds] = useState(() => new Set());
  const [missTap, setMissTap] = useState(null);
  const [runId, setRunId] = useState(0);
  const [allFound, setAllFound] = useState([]); // {sceneTitle, label, explanation} across the whole game
  const [checkedTargetIds, setCheckedTargetIds] = useState(() => new Set());
  const missTimerRef = useRef(null);

  useEffect(() => { trackGameEvent("game_opened", { game: "spot-the-issue" }); }, [runId]);

  const round = ROUNDS[roundIndex];
  const finished = roundIndex >= ROUNDS.length;
  const items = useMemo(
    () => (round ? (round.type === "spot" ? round.scene.issues : round.scene.differences) : []),
    [round],
  );
  const roundDone = round ? items.every((it) => foundIds.has(it.id)) : false;
  const keyboardTargets = useMemo(() => buildKeyboardTargets(items), [items]);

  useEffect(() => {
    if (round && roundDone && items.length > 0) {
      trackGameEvent("spot_the_issue_round_completed", { game: "spot-the-issue", round: round.scene.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundDone]);

  function handleTap({ x_pct, y_pct }) {
    // Some authored hotspots sit close enough that their hit-radii touch or
    // overlap: pick the CLOSEST eligible issue rather than the first one
    // in authoring order, so a tap near the boundary always credits the
    // issue it's actually nearest to.
    const eligible = items.filter((it) => !foundIds.has(it.id) && distancePct({ x_pct, y_pct }, it) <= it.radiusPct);
    const hit = eligible.length
      ? eligible.reduce((closest, it) =>
          distancePct({ x_pct, y_pct }, it) < distancePct({ x_pct, y_pct }, closest) ? it : closest)
      : null;
    if (hit) {
      setFoundIds((prev) => new Set(prev).add(hit.id));
      setAllFound((prev) => [...prev, { sceneTitle: round.scene.title, label: hit.label, explanation: hit.explanation }]);
      setMissTap(null);
    } else {
      setMissTap({ x_pct, y_pct });
      window.clearTimeout(missTimerRef.current);
      missTimerRef.current = window.setTimeout(() => setMissTap(null), 700);
    }
  }

  useEffect(() => () => window.clearTimeout(missTimerRef.current), []);

  function handleKeyboardTarget(target) {
    setCheckedTargetIds((prev) => new Set(prev).add(target.id));
    handleTap({ x_pct: target.x_pct, y_pct: target.y_pct });
  }

  function nextRound() {
    setRoundIndex((i) => i + 1);
    setFoundIds(new Set());
    setMissTap(null);
    setCheckedTargetIds(new Set());
  }

  function replay() {
    setRoundIndex(0);
    setFoundIds(new Set());
    setMissTap(null);
    setAllFound([]);
    setCheckedTargetIds(new Set());
    setRunId((n) => n + 1);
  }

  useEffect(() => {
    if (finished) {
      trackGameEvent("spot_the_issue_completion", { game: "spot-the-issue", total_found: allFound.length });
      trackGameEvent("game_completed", { game: "spot-the-issue" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const foundMarkers = useMemo(
    () => items
      .filter((it) => foundIds.has(it.id))
      .map((it, idx) => ({ id: it.id, x_pct: it.x_pct, y_pct: it.y_pct, severity: "medium", tag_label: it.label, status: "active", _idx: idx })),
    [items, foundIds],
  );

  return (
    <GamesPageShell
      title="Spot the Issue"
      description="Tap the problems you'd want to catch before a rental starts: or spot what changed between pickup and return."
    >
      {finished ? (
        <FinishedView allFound={allFound} onReplay={replay} />
      ) : (
        <div className="space-y-5" data-testid="spot-the-issue-playing">
          <div className="flex justify-between text-xs text-slate-500">
            <span data-testid="spot-round-text">Scene {roundIndex + 1} of {ROUNDS.length}</span>
            <span data-testid="spot-found-count">{foundIds.size} of {items.length} found</span>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="font-display text-lg font-semibold">{round.scene.title}</div>
            <p className="text-sm text-slate-600 mt-1">{round.scene.intro}</p>

            <p className="sr-only">
              The diagram below is a free-tap surface best used with a mouse or touchscreen.
              If you're using a keyboard or screen reader, use the "Keyboard-accessible mode"
              check points listed after the diagram instead: they check the same spots and
              give the same found or not-found feedback.
            </p>

            {round.type === "spot" ? (
              <div className="mt-4">
                <CarDiagram view={round.scene.view} damages={foundMarkers} readOnly={false} onClickEmpty={handleTap} />
              </div>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1 text-center">At pickup</div>
                  <CarDiagram view={round.scene.view} damages={foundMarkers} readOnly={false} onClickEmpty={handleTap} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1 text-center">At return</div>
                  <CarDiagram view={round.scene.view} damages={foundMarkers} readOnly={false} onClickEmpty={handleTap} />
                </div>
              </div>
            )}

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="spot-keyboard-mode">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1">
                Keyboard-accessible mode
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Not using a mouse or touchscreen? Use these check points instead: each one checks a
                different spot on the car and gives you the same found / not-found feedback as tapping the diagram.
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Check points on the diagram">
                {keyboardTargets.map((t, idx) => {
                  const isRealFound = !t.isDecoy && foundIds.has(t.id);
                  const isChecked = checkedTargetIds.has(t.id);
                  const statusSuffix = isRealFound ? ": found" : isChecked ? ": already checked, nothing there" : "";
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`spot-kbd-target-${idx}`}
                      disabled={isRealFound}
                      onClick={() => handleKeyboardTarget(t)}
                      aria-label={`Check point ${idx + 1} of ${keyboardTargets.length}${statusSuffix}`}
                      className={`min-h-11 min-w-11 px-3 rounded-lg border text-sm font-medium transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2
                        ${isRealFound
                          ? "border-emerald-400 bg-emerald-100 text-emerald-800 underline decoration-2 underline-offset-2"
                          : isChecked
                          ? "border-slate-300 bg-slate-100 text-slate-400 line-through"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}`}
                    >
                      {isRealFound ? "✓ " : ""}{idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            {missTap && (
              <p className="text-xs text-slate-500 mt-2 text-center" role="status" data-testid="spot-miss-feedback">Not quite: try another spot.</p>
            )}

            {/* Found-so-far explanations: doubles as the accessible, text-based feedback trail */}
            {foundIds.size > 0 && (
              <div className="mt-4 space-y-2" data-testid="spot-found-list">
                {items.filter((it) => foundIds.has(it.id)).map((it) => (
                  <div key={it.id} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <div>
                      <div className="text-sm font-medium text-emerald-900">{it.label}</div>
                      <div className="text-xs text-emerald-800 mt-0.5 leading-relaxed">{it.explanation}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {roundDone && (
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  data-testid="spot-continue"
                  onClick={nextRound}
                  className="min-h-11 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027]"
                >
                  {roundIndex + 1 >= ROUNDS.length ? "See results" : "Next scene"} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </GamesPageShell>
  );
}

function FinishedView({ allFound, onReplay }) {
  return (
    <GameResultCard
      icon={Search}
      tone="emerald"
      eyebrow="Game complete"
      headline={`${allFound.length} found`}
      subtext="Here's everything you caught, in order, and why each one matters."
      onReplay={onReplay}
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="spot-review-section">
        <div className="font-display text-lg font-semibold">What you found</div>
        <div className="mt-3 space-y-3">
          {allFound.map((f, idx) => (
            <div key={idx} className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs text-slate-500">{f.sceneTitle}</div>
              <div className="text-sm text-slate-800 font-medium mt-0.5">{f.label}</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">{f.explanation}</div>
            </div>
          ))}
        </div>
      </div>
    </GameResultCard>
  );
}
